import React, { useMemo, useState } from "react"

import { format } from "date-fns"
import {
  Calendar,
  CalendarClock,
  CalendarDays,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Edit,
  MapPin,
} from "lucide-react"

import Icon from "@/components/Icon"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Textarea } from "@/components/ui/textarea"
import type { ApplicationDetails } from "@/hooks/useApplicationDetails"
import { useCancelOrientationBooking } from "@/hooks/useCancelOrientationBooking"
import { sortBookingsByStatusAndDate } from "@/utils/sessionBookings"

import { PermissionGuard } from "../common/PermissionGuard"

interface BookingsListProps {
  applicationId: string
  orientationBookings: ApplicationDetails["orientation_bookings"]
  vehicleBookings: ApplicationDetails["vehicle_bookings"]
  onScheduleOrientation?: () => void
  onScheduleVehicleCollection?: () => void
  onRescheduleOrientation?: (bookingId: string) => void
  onRescheduleVehicleCollection?: (bookingId: string) => void
}

const BookingsList = ({
  applicationId,
  orientationBookings,
  vehicleBookings,
  onScheduleOrientation,
  onScheduleVehicleCollection,
  onRescheduleOrientation,
  onRescheduleVehicleCollection,
}: BookingsListProps) => {
  const [orientationOpen, setOrientationOpen] = useState(true)
  const [vehicleOpen, setVehicleOpen] = useState(true)
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null)
  const [cancellationReason, setCancellationReason] = useState("")
  const { mutate: cancelOrientationBooking, isPending: isCancelling } =
    useCancelOrientationBooking()

  const handleConfirmCancel = () => {
    if (!cancellingBookingId) return

    cancelOrientationBooking(
      { bookingId: cancellingBookingId, applicationId, reason: cancellationReason.trim() },
      {
        onSuccess: () => {
          setCancellingBookingId(null)
          setCancellationReason("")
        },
      },
    )
  }

  // Format date for better display including time
  const formatBookingDate = (dateStr: string) => {
    const date = new Date(dateStr)
    // Format date and time without timezone conversion
    return format(date, "EEE, MMM d, yyyy 'at' h:mm a")
  }

  const isBeforeSessionStart = (dateStr: string) => new Date(dateStr).getTime() > Date.now()

  const sortedOrientationBookings = useMemo(
    () => sortBookingsByStatusAndDate(orientationBookings ?? []),
    [orientationBookings],
  )
  const sortedVehicleBookings = useMemo(
    () => sortBookingsByStatusAndDate(vehicleBookings ?? []),
    [vehicleBookings],
  )

  return (
    <>
      {/* Orientation Bookings Section */}
      <Card className="mb-6 shadow-sm">
        <Collapsible open={orientationOpen} onOpenChange={setOrientationOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-semibold">Orientation Bookings</CardTitle>
                </div>
                {orientationOpen ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {orientationBookings && orientationBookings.length > 0 ? (
                <div className="space-y-4">
                  {sortedOrientationBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="border rounded-md p-4 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 w-full">
                          <div className="flex justify-between w-full items-center gap-2">
                            <p className="font-medium text-primary flex items-center gap-1">
                              <CalendarClock className="h-4 w-4" />
                              {formatBookingDate(booking.date)}
                            </p>
                            <div className="flex items-center gap-2">
                              {booking.cancelled_at ? (
                                <Badge
                                  variant="outline"
                                  className="bg-muted text-muted-foreground border-muted-foreground/30 flex items-center gap-1"
                                >
                                  <Icon name="xCircle" className="h-3 w-3" /> Cancelled
                                </Badge>
                              ) : booking.attended == null ? (
                                <>
                                  <Badge
                                    variant="outline"
                                    className="bg-primary text-black border-primary flex items-center gap-1"
                                  >
                                    <CalendarDays className="h-3 w-3" /> Scheduled
                                  </Badge>
                                  <PermissionGuard
                                    resource="session_bookings"
                                    action="update"
                                    scopes={["own", "any"]}
                                    fallback={null}
                                  >
                                    {onRescheduleOrientation && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center h-7 px-2 text-xs"
                                        onClick={() => onRescheduleOrientation(booking.id)}
                                      >
                                        <Edit className="h-3 w-3" />
                                        Reschedule
                                      </Button>
                                    )}
                                    {isBeforeSessionStart(booking.date) && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center h-7 px-2 text-xs text-destructive hover:text-destructive"
                                        onClick={() => setCancellingBookingId(booking.id)}
                                      >
                                        <Icon name="xCircle" className="h-3 w-3" />
                                        Cancel
                                      </Button>
                                    )}
                                  </PermissionGuard>
                                </>
                              ) : booking.attended ? (
                                <Badge
                                  variant="outline"
                                  className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1"
                                >
                                  <CheckCircle2 className="h-3 w-3" /> Attended
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1"
                                >
                                  <Icon name="xCircle" className="h-3 w-3" /> Not Attended
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm font-medium flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {booking.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No orientation bookings found for this applicant.</p>
                  <PermissionGuard
                    resource="session_bookings"
                    action="insert"
                    scopes={["own", "any"]}
                    fallback={null}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={onScheduleOrientation}
                    >
                      Schedule Orientation
                    </Button>
                  </PermissionGuard>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Vehicle Collection Booking Section */}
      <Card className="shadow-sm">
        <Collapsible open={vehicleOpen} onOpenChange={setVehicleOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-4">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  <Car className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-semibold">
                    Vehicle Collection Booking
                  </CardTitle>
                </div>
                {vehicleOpen ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              {vehicleBookings && vehicleBookings.length > 0 ? (
                <div className="space-y-4">
                  {sortedVehicleBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="border rounded-md p-4 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-2 w-full">
                          <div className="flex justify-between w-full items-center gap-2">
                            <p className="font-medium text-primary flex items-center gap-1">
                              <CalendarClock className="h-4 w-4" />
                              {formatBookingDate(booking.date)}
                            </p>
                            <div className="flex items-center gap-2">
                              {booking.attended == null ? (
                                <>
                                  <Badge
                                    variant="outline"
                                    className="bg-primary text-black border-primary flex items-center gap-1"
                                  >
                                    <CalendarDays className="h-3 w-3" /> Scheduled
                                  </Badge>
                                  <PermissionGuard
                                    resource="session_bookings"
                                    action="update"
                                    scopes={["own", "any"]}
                                    fallback={null}
                                  >
                                    {onRescheduleVehicleCollection && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="flex items-center h-7 px-2 text-xs"
                                        onClick={() => onRescheduleVehicleCollection(booking.id)}
                                      >
                                        <Edit className="h-3 w-3" />
                                        Reschedule
                                      </Button>
                                    )}
                                  </PermissionGuard>
                                </>
                              ) : booking.attended ? (
                                <Badge
                                  variant="outline"
                                  className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1"
                                >
                                  <CheckCircle2 className="h-3 w-3" /> Attended
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="bg-red-100 text-red-800 border-red-200 flex items-center gap-1"
                                >
                                  <Icon name="xCircle" className="h-3 w-3" /> Not Attended
                                </Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-sm font-medium flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {booking.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Car className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No vehicle bookings found for this applicant.</p>
                  <PermissionGuard
                    resource="session_bookings"
                    action="insert"
                    scopes={["own", "any"]}
                    fallback={null}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={onScheduleVehicleCollection}
                    >
                      Schedule Vehicle Collection
                    </Button>
                  </PermissionGuard>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <AlertDialog
        open={cancellingBookingId != null}
        onOpenChange={(open) => {
          if (!open) {
            setCancellingBookingId(null)
            setCancellationReason("")
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Orientation Booking</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel the orientation booking. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Reason for cancellation (optional)"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            disabled={isCancelling}
          />
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setCancellingBookingId(null)
                setCancellationReason("")
              }}
              disabled={isCancelling}
            >
              Keep Booking
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel} disabled={isCancelling}>
              {isCancelling ? "Cancelling..." : "Cancel Booking"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default BookingsList
